import { useState, useEffect } from 'react';

/**
 * Custom React hook for managing light/dark theme with localStorage persistence
 * and system preference detection.
 *
 * Features:
 * - Persists theme preference to localStorage
 * - Detects and respects system color scheme preference
 * - Listens for system preference changes (only if user hasn't set manual preference)
 * - Applies theme to document root element via data-theme attribute
 * - Provides toggle function for easy theme switching
 *
 * @returns {Object} { theme: 'light' | 'dark', toggleTheme: Function }
 *
 * @example
 * function App() {
 *   const { theme, toggleTheme } = useTheme();
 *   return (
 *     <button onClick={toggleTheme}>
 *       Current theme: {theme}
 *     </button>
 *   );
 * }
 */
const useTheme = () => {
  /**
   * Helper function to determine the initial theme value.
   * Priority order:
   * 1. localStorage saved preference
   * 2. System preference (prefers-color-scheme)
   * 3. Default to 'light'
   *
   * @returns {'light' | 'dark'} The initial theme value
   */
  const getInitialTheme = () => {
    // Check localStorage for saved preference
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme) {
      return savedTheme;
    }

    // Fall back to system preference
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    if (prefersDark) {
      return 'dark';
    }

    // Default to light theme
    return 'light';
  };

  // Initialize theme state with the determined initial value
  const [theme, setTheme] = useState(getInitialTheme);

  // Effect to apply theme changes to the DOM and persist to localStorage
  useEffect(() => {
    // Set the data-theme attribute on the root element
    document.documentElement.setAttribute('data-theme', theme);

    // Persist the theme preference to localStorage
    localStorage.setItem('theme', theme);
  }, [theme]);

  // Effect to listen for system preference changes
  useEffect(() => {
    // Create media query for dark mode preference
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    // Handler for system preference changes
    // Only responds if user hasn't manually set a preference
    const handleChange = (e) => {
      // Check at event time whether manual preference exists
      if (localStorage.getItem('theme') === null) {
        setTheme(e.matches ? 'dark' : 'light');
      }
    };

    // Add event listener for changes
    mediaQuery.addEventListener('change', handleChange);

    // Cleanup: always remove event listener on unmount
    return () => {
      mediaQuery.removeEventListener('change', handleChange);
    };
  }, []);

  /**
   * Toggles between light and dark theme.
   * Sets manual preference in localStorage.
   */
  const toggleTheme = () => {
    setTheme((prevTheme) => (prevTheme === 'light' ? 'dark' : 'light'));
  };

  // Return the current theme and toggle function
  return { theme, toggleTheme };
};

export default useTheme;
